<template>
  <DpaNewsBaseModal ref="modal" @close="handleClose" @cancel.prevent>
    <template #header>
      <span class="text-sm">{{ currentPageLabel }}</span>
    </template>
    <video
      v-if="currentPageMedia.type == 'video'"
      controls
      autoplay
      :src="currentPageMedia.path"
      class="mb-6 aspect-video w-full rounded-lg"></video>
    <img
      v-if="currentPageMedia.type == 'image'"
      :src="currentPageMedia.path"
      :alt="`${t('onboarding.illustration')} ${t(`onboarding.${currentPageIdx}.title`)}`"
      class="mb-6 aspect-video w-full rounded-lg border border-neutral-faint object-contain" />
    <div class="flex flex-col items-center gap-4">
      <h1 class="text-center text-2xl font-medium text-neutral-primary">
        {{ t(`onboarding.${currentPageIdx}.title`) }}
      </h1>
      <p class="text-neutral-subtle">
        {{ t(`onboarding.${currentPageIdx}.description`) }}
      </p>
    </div>
    <template #footer>
      <UiButton
        appearance="outline-emphasis"
        class="justify-center"
        data-testid="cancelButton"
        size="md"
        @click="handleBackwards">
        {{ cancelButtonText }}
      </UiButton>
      <UiButton
        appearance="fill-emphasis"
        class="justify-center"
        data-testid="submitButton"
        @click="handleForwards">
        {{ submitButtonText }}
      </UiButton>
    </template>
  </DpaNewsBaseModal>
</template>

<script setup lang="ts">
import UiButton from "@dpa-id-components/dpa-shared-components/src/components/UiButton/UiButton.vue";
import { computed, onMounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import DpaNewsBaseModal from "@/components/shared/DpaNewsBaseModal/DpaNewsBaseModal.vue";
import Cvd from "@/components/shared/DpaNewsWelcomeModal/assets/cvd.mp4";
import DeskEmptyState from "@/components/shared/DpaNewsWelcomeModal/assets/desk-empty-state.mp4";
import Desk from "@/components/shared/DpaNewsWelcomeModal/assets/desk.mp4";
import KiRecherche from "@/components/shared/DpaNewsWelcomeModal/assets/ki-recherche.mp4";
import Messenger from "@/components/shared/DpaNewsWelcomeModal/assets/messenger.png";
import Newsfeed from "@/components/shared/DpaNewsWelcomeModal/assets/newsfeed.mp4";
import { useUserPreferences } from "@/composition/api/authorization.ts";
import { useTracking } from "@/composition/shared/useTracking.ts";
import { useUserStore } from "@/stores/user.ts";

const pageMedia: Array<{ type: "video" | "image"; path: string }> = [
  { type: "video", path: DeskEmptyState },
  { type: "video", path: Desk },
  { type: "video", path: Cvd },
  { type: "video", path: Newsfeed },
  { type: "image", path: Messenger },
  { type: "video", path: KiRecherche },
];
const MAX_PAGE_INDEX = pageMedia.length - 1;
const { trackEvent } = useTracking();
const user = useUserStore();
const { t } = useI18n();
const { execute: changeUserPreferences } = useUserPreferences({
  preferredHomepage: null,
  preferredCvdPage: null,
  preferredLanguage: null,
  suppressOnboardingTour: true,
});

const modal = useTemplateRef<InstanceType<typeof DpaNewsBaseModal>>("modal");

const currentPageIdx = ref(0);

const currentPageLabel = computed(() => {
  return `${currentPageIdx.value + 1} ${t("onboarding.xOfY")} ${MAX_PAGE_INDEX + 1}`;
});
const currentPageMedia = computed(() => pageMedia[currentPageIdx.value]);
const submitButtonText = computed(() =>
  t(`onboarding.${currentPageIdx.value}.submitBtnText`),
);
const cancelButtonText = computed(() =>
  t(`onboarding.${currentPageIdx.value}.cancelBtnText`),
);

async function handleForwards() {
  trackEvent(
    "onboardingModal_click_button_submit",
    `step ${currentPageLabel.value}  | ${submitButtonText.value}`,
  );
  if (currentPageIdx.value === MAX_PAGE_INDEX) {
    await suppressWelcomeModal();
  } else {
    currentPageIdx.value += 1;
  }
}

async function handleBackwards() {
  trackEvent(
    "onboardingModal_click_button_cancel",
    `step ${currentPageLabel.value}  | ${cancelButtonText.value}`,
  );
  if (currentPageIdx.value === 0) {
    await suppressWelcomeModal();
  } else {
    currentPageIdx.value -= 1;
  }
}

async function handleClose() {
  trackEvent(
    "onboardingModal_click_button_iconButton",
    `step ${currentPageLabel.value}  | Cancel Onboarding Tour`,
  );
  await suppressWelcomeModal();
}

async function suppressWelcomeModal() {
  await changeUserPreferences();
  user.preferences.suppressOnboardingTour = true;
}

onMounted(() => {
  modal.value?.showModal();
});
</script>
